/* eslint-disable react/prop-types */
import React, { forwardRef } from "react"
import type * as Polymorphic from "@radix-ui/react-polymorphic"
import cx from "clsx"

export interface IconButtonProps {
  variant: `filled` | `outlined` | `standard`
  size: `small` | `default` | `large`
  inline: boolean
}

type PolymorphicIconButton = Polymorphic.ForwardRefComponent<
  `button`,
  Partial<IconButtonProps>
>

const sizeStyles: Record<IconButtonProps[`size`], string> = {
  small: `w-4 h-4 leading-snug text-sm tracking-wide`,
  default: `w-6 h-6 hleading-normal text-sm tracking-wide`,
  large: `w-8 h-8 leading-normal tracking-wider`
}

const IconButton = forwardRef(
  (
    {
      as: Component = `button`,
      className,
      variant = `outlined`,
      size = `default`,
      inline = true,
      ...rest
    },
    ref
  ) => (
    <Component
      ref={ref}
      className={cx(
        `rounded-full border justify-center items-center disabled:cursor-not-allowed transition-all`,
        sizeStyles[size],
        {
          "bg-black text-white border-black hover:bg-opacity-70 active:bg-opacity-80 active:border-opacity-80 disabled:bg-primary-mid-gray disabled:text-white disabled:border-primary-mid-gray":
            variant === `filled`,
          "bg-white text-black border-black hover:bg-primary-lightest-gray focus:bg-primary-light-gray active:bg-primary-light-gray disabled:text-primary-mid-gray disabled:border-primary-mid-gray":
            variant === `outlined`,
          "bg-transparent text-black border-transparent hover:bg-primary-lightest-gray focus:bg-primary-light-gray active:bg-primary-light-gray disabled:text-primary-mid-gray":
            variant === `standard`,
          "inline-flex": inline,
          flex: !inline
        },
        className
      )}
      {...rest}
    />
  )
) as PolymorphicIconButton

export default IconButton
